import React, { FC, useCallback, useMemo } from 'react';
import {
  Avatar,
  Badge,
  Box,
  ButtonBase,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  alpha,
  chipClasses,
  lighten,
  listItemIconClasses,
  listItemTextClasses,
  useTheme,
} from '@mui/material';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import NotificationIcon from '@mui/icons-material/NotificationsNoneOutlined';
import HelpIcon from '@mui/icons-material/InfoOutlined';
import InteractiveTooltip from '../../components/InteractiveTooltip';
import {
  appViewSetUser,
  appViewUpdateIsUserMenuOpen,
  getAppViewRelevantIsUserMenuOpen,
  getAppViewUserInboxesCount,
  getAppViewUserName,
  getAppViewUserProfileImage,
} from '../../app/store/appViewSlice';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import SpaceBarIcon from '@mui/icons-material/SpaceBarOutlined';
import ArrowIcon from '@mui/icons-material/TransitEnterexitOutlined';
import { googleLogout } from '@react-oauth/google';
import { User } from '../../app/store/appViewSlice/types';
import { RootState } from '../../store';
import { routingOnRouteRequest } from 'src/api/store/routingSlice';
import { Routes } from 'src/routes';
import { settingsViewOnChangeActiveTab } from 'src/routes/Settings/store/settingsViewSlice';
import { SettingsTabs } from 'src/routes/Settings/store/settingsViewSlice/types';
import { LocalStorageKeys } from 'src/viewsTypes';
import { TooltipComponentsSx } from 'src/components/InteractiveTooltip/domain';
import { transition } from 'src/theme';
import { authService } from '../../services/authService';

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

const BarActions: FC<Props> = ({
  profileImage,
  userName,
  isMenuOpen,
  inboxCount,
  setUser,
  goToSettings,
  goToAccountSettings,
  toggleIsUserMenuOpen,
}) => {
  const theme = useTheme();
  const performLogout = useCallback(() => {
    // Clear the authentication token
    authService.clearToken();

    // Clear user-related data from localStorage
    localStorage.removeItem(LocalStorageKeys.UserId);
    localStorage.removeItem(LocalStorageKeys.InboxId);
    localStorage.removeItem(LocalStorageKeys.Email);
    localStorage.removeItem('user_firstName');
    localStorage.removeItem('user_lastName');
    localStorage.removeItem('user_companyName');

    // Still call googleLogout to clean up any Google state
    googleLogout();

    // Reset the user in the app
    setUser(null);
  }, [setUser]);

  const withMenuClose = useCallback(
    (...actions: Array<() => void>) =>
      () => {
        toggleIsUserMenuOpen();
        actions.forEach((action) => action());
      },
    [toggleIsUserMenuOpen],
  );

  const onLogOut = useCallback(withMenuClose(performLogout), [
    withMenuClose,
    performLogout,
  ]);

  const onGoToSettings = useCallback(withMenuClose(goToSettings), [
    withMenuClose,
    goToSettings,
  ]);

  const onGoToAccountSettings = useCallback(
    withMenuClose(goToAccountSettings),
    [withMenuClose, goToAccountSettings],
  );

  const UserTrigger = useMemo(
    () => (
      <IconButton onClick={toggleIsUserMenuOpen} size="small">
        <Avatar
          src={profileImage ?? undefined}
          sx={{
            height: ({ spacing }) => spacing(3),
            width: ({ spacing }) => spacing(3),
            bgcolor: ({ palette }) => alpha(palette.primary.light, 0.25),
            color: ({ palette }) => palette.common.white,
          }}
        />
      </IconButton>
    ),
    [profileImage, toggleIsUserMenuOpen],
  );

  const componentsSx: TooltipComponentsSx = useMemo(() => {
    return {
      tooltip: {
        backgroundColor: '#f5f5f9',
        color: 'rgba(0, 0, 0, 0.87)',
        borderRadius: '10px',
        boxShadow:
          'rgba(14, 30, 37, 0.12) 0px 2px 4px 0px, rgba(14, 30, 37, 0.32) 0px 2px 16px 0px',
      },
      arrow: {
        color: alpha(theme.palette.primary.main, 0.5),
      },
    };
  }, []);

  const listItems = [
    {
      onClick: onGoToSettings,
      icon: <SettingsIcon />,
      primaryText: 'החשבון שלי',
      iconClass: listItemIconClasses.root,
      iconHoverStyle: {
        transform: 'rotateZ(-120deg)',
      },
    },
    {
      onClick: onLogOut,
      icon: (
        <Box position="relative" width={24}>
          <ArrowIcon
            className="logout-arrow-icon"
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              fontSize: 20,
              transition: transition(),
              transform: 'translate(calc(-50% + 4px),-50%) rotateZ(-135deg)',
            }}
          />
          <SpaceBarIcon
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(calc(-50% - 4px),-50%) rotateZ(90deg)',
            }}
          />
        </Box>
      ),
      primaryText: 'התנתק',
      iconClass: 'logout-arrow-icon',
      iconHoverStyle: {
        transform: 'translate(calc(-50% + 6px),-50%) rotateZ(-135deg)',
      },
    },
  ];

  const UserMenuContent = (
    <Stack direction="column" width={185}>
      <ButtonBase onClick={onGoToAccountSettings} sx={{ width: '100%' }}>
        <Stack
          direction="row-reverse"
          width="100%"
          px={1}
          py={1.25}
          gap={1.5}
          sx={{
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            bgcolor: ({ palette }) => alpha(palette.primary.main, 0.25),
          }}
        >
          <Avatar
            src={profileImage ?? undefined}
            sx={{
              height: ({ spacing }) => spacing(6),
              width: ({ spacing }) => spacing(6),
              color: ({ palette }) => palette.common.white,
              boxShadow: ({ shadows }) => shadows[5],
            }}
          />
          <Stack direction="column" alignItems="flex-end">
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: 18 }}>
              {userName}
            </Typography>
            <Chip
              label="PREMIUM"
              variant="filled"
              sx={{
                fontFamily: 'Big Shoulders Display',
                letterSpacing: 0.5,
                height: 'fit-content',
                width: 'fit-content',
                fontSize: 10,
                opacity: 0.7,
                background: ({ palette }) =>
                  lighten(palette.secondaryOrange ?? '', 0.25),
                border: ({ palette }) => `1px solid ${palette.divider}`,
                padding: ({ spacing }) => spacing(0.25, 1.125),
                [`& .${chipClasses.label}`]: {
                  padding: 0,
                  // color: '#546e7a',
                  opacity: 0.85,
                },
              }}
            />
          </Stack>
        </Stack>
      </ButtonBase>
      <Divider flexItem />
      <List
        sx={{
          flex: 1,
          p: 0,
          bgcolor: ({ palette }) => alpha(palette.primary.light, 0.075),
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
        }}
      >
        {listItems.map((item, index) => {
          const isLast = index === listItems.length - 1;
          return (
            <ListItemButton
              key={index}
              sx={{
                display: 'flex',
                gap: ({ spacing }) => spacing(1.5),
                ...(isLast
                  ? {
                      borderBottomLeftRadius: '10px',
                      borderBottomRightRadius: '10px',
                    }
                  : {}),
                [`&:hover .${item.iconClass ?? ''}`]: {
                  ...(item.iconHoverStyle ?? {}),
                },
              }}
              onClick={item.onClick}
            >
              <ListItemIcon
                sx={{ minWidth: 'unset', transition: transition() }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                sx={{
                  [`& .${listItemTextClasses.primary}`]: {
                    textAlign: 'start',
                    fontSize: 18,
                    lineHeight: 1,
                  },
                }}
                primary={item.primaryText}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Stack>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: ({ spacing }) => spacing(1),
        flexDirection: 'row-reverse',
      }}
    >
      <InteractiveTooltip
        overrideIsOpen={isMenuOpen}
        setOverrideIsOpen={toggleIsUserMenuOpen}
        isWithArrow
        targetPlacement="bottom"
        tooltipOffsets={{ x: 65 }}
        Trigger={UserTrigger}
        Contents={UserMenuContent}
        componentsSx={componentsSx}
      />
      <IconButton>
        <Badge
          color="secondary"
          variant="dot"
          badgeContent=" "
          overlap="circular"
        >
          <NotificationIcon />
        </Badge>
      </IconButton>
      <IconButton>
        <HelpIcon />
      </IconButton>
    </Box>
  );
};

const mapStateToProps = (state: RootState) => ({
  inboxCount: getAppViewUserInboxesCount(state),
  userName: getAppViewUserName(state),
  profileImage: getAppViewUserProfileImage(state),
  isMenuOpen: getAppViewRelevantIsUserMenuOpen(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  toggleIsUserMenuOpen: () => dispatch(appViewUpdateIsUserMenuOpen()),
  setUser: (user: User | null) => dispatch(appViewSetUser(user)),
  goToSettings: () => {
    dispatch(routingOnRouteRequest({ path: Routes.SettingsProfile }));
  },
  goToAccountSettings: () => {
    dispatch(routingOnRouteRequest({ path: Routes.SettingsProfile }));
    dispatch(settingsViewOnChangeActiveTab(SettingsTabs.Profile));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(BarActions);
